// Utility functions for managing wizard state in localStorage

const WIZARD_STORAGE_KEY = "account-setup-wizard";

export interface WizardState {
    currentStep: number;
    completedSteps: number[];
    accountView?: string;
    userEmail?: string;
    timestamp: number;
}

export const saveWizardState = (state: Partial<WizardState>): void => {
    try {
        const existingState = getWizardState();
        const newState: WizardState = {
            ...existingState,
            ...state,
            timestamp: Date.now(),
        };
        localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(newState));
    } catch (error) {
        console.warn("Failed to save wizard state to localStorage:", error);
    }
};

export const getWizardState = (): WizardState => {
    try {
        const stored = localStorage.getItem(WIZARD_STORAGE_KEY);
        if (stored) {
            const state = JSON.parse(stored) as WizardState;

            // resetting state after 24 hours
            const isExpired = Date.now() - state.timestamp > 24 * 60 * 60 * 1000;
            if (isExpired) {
                clearWizardState();
                return getDefaultWizardState();
            }

            return state;
        }
    } catch (error) {
        console.warn("Failed to load wizard state from localStorage:", error);
    }

    return getDefaultWizardState();
};

export const clearWizardState = (): void => {
    try {
        localStorage.removeItem(WIZARD_STORAGE_KEY);
    } catch (error) {
        console.warn("Failed to clear wizard state from localStorage:", error);
    }
};

const getDefaultWizardState = (): WizardState => ({
    currentStep: 0,
    completedSteps: [],
    timestamp: Date.now(),
});

export const updateWizardStep = (step: number): void => {
    const currentState = getWizardState();
    saveWizardState({
        currentStep: step,
        completedSteps: step > currentState.currentStep ? [...currentState.completedSteps, currentState.currentStep] : currentState.completedSteps,
    });
};

export const updateAccountView = (accountView: string): void => {
    saveWizardState({ accountView });
};

export const updateUserEmail = (email: string): void => {
    saveWizardState({ userEmail: email });
};

export const isWizardInProgress = (): boolean => {
    const state = getWizardState();
    return state.currentStep > 0 || state.completedSteps.length > 0;
};
